import { useTranslation } from 'react-i18next';
import type { ReactNode } from 'react';
import { cellPieces, findReferences, type CellPiece, type CellTarget } from '../../services/excel/cellRefs';

type SelectRange = (sheet: string, address: string) => void;

/** A code span in the agent's prose, as a link to the place it names.
 *
 *  The pane contract already asks for every reference as code (`output-SPV!J813`), so the markup is there;
 *  `cellTarget` decides which of those spans is really an address. A span that names no sheet the workbook
 *  has stays exactly what it was — plain code — which is what keeps `P50`, `SUM` and a tool's name from
 *  turning into links to nowhere.
 *
 *  A span that lists places (`input!D180:K185 et D187:K198`) gets one link per place, the words between
 *  them left as they are (`cellPieces`).
 *
 *  It renders as code either way. The difference is the accent and the underline on hover: the sentence
 *  reads the same, and the places in it are reachable. */
export function CellLink({ children, sheets, onSelectRange, className = '', ...rest }: {
  children?: ReactNode;
  sheets: readonly string[];
  onSelectRange?: SelectRange;
  className?: string;
} & Record<string, unknown>) {
  // A fenced block arrives here too (ReactMarkdown routes both through `code`); only a one-line span can
  // be an address, and `cellTarget` rejects anything with a newline.
  const text = typeof children === 'string' ? children : Array.isArray(children) && children.every((c) => typeof c === 'string') ? children.join('') : null;
  const pieces = text && onSelectRange ? cellPieces(text, sheets) : null;
  if (!pieces || !onSelectRange) {
    return (
      <code className={className} {...rest}>
        {children}
      </code>
    );
  }
  return (
    <code className={className} {...rest}>
      <Pieces pieces={pieces} onSelectRange={onSelectRange} />
    </code>
  );
}

/** A card's text — a finding's fix, a plan's note, why a driver is dormant, a question — with the places it
 *  names as links, the rest as it is.
 *
 *  A card's text is not markdown, so nothing marks a place up as code: it is found in the words, by the rule the
 *  prose of an answer follows (`findReferences`): a place names a sheet the workbook has, so `P50` stays text, and
 *  nothing is a link while the sheet list is still empty or the thread cannot select (no `onSelectRange`). */
export function CellText({ text, sheets, onSelectRange }: {
  text: string;
  sheets?: readonly string[];
  onSelectRange?: SelectRange;
}) {
  const found = onSelectRange && sheets ? findReferences(text, sheets) : [];
  if (!found.length || !onSelectRange || !sheets) return <>{text}</>;
  const pieces: CellPiece[] = [];
  let last = 0;
  for (const { start, end } of found) {
    if (start > last) pieces.push({ text: text.slice(last, start), target: null });
    pieces.push(...(cellPieces(text.slice(start, end), sheets) ?? []));
    last = end;
  }
  if (last < text.length) pieces.push({ text: text.slice(last), target: null });
  return <Pieces pieces={pieces} onSelectRange={onSelectRange} />;
}

function Pieces({ pieces, onSelectRange }: { pieces: CellPiece[]; onSelectRange: SelectRange }) {
  return <>{pieces.map((p, i) => (p.target ? <Place key={i} target={p.target} label={p.text} onSelectRange={onSelectRange} /> : p.text))}</>;
}

/** One place, as the button that selects it in Excel: the accent and the underline on hover. */
function Place({ target, label, onSelectRange }: { target: CellTarget; label: string; onSelectRange: SelectRange }) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      data-testid="cell-link"
      title={t('chat.selectInExcel', { address: target.address ? `${target.sheet}!${target.address}` : target.sheet })}
      onClick={() => onSelectRange(target.sheet, target.address)}
      className="cursor-pointer font-mono text-accent underline-offset-2 hover:text-accent-dark hover:underline"
    >
      {label}
    </button>
  );
}
